import React from "react";
import { Heart, ArrowRight, Sparkles, Users, Globe } from "lucide-react";

const GIVE_URL = "https://adventistgiving.org/donate/ANWFG2";

const IMPACT = [
  { icon: Users, title: "Local Ministry", desc: "Supports children, youth, and family programs that disciple the next generation." },
  { icon: Heart, title: "Pastoral Care", desc: "Funds visitation, counseling, and spiritual support for our church family." },
  { icon: Globe, title: "Mission & Outreach", desc: "Extends Christ's love beyond our walls — feeding, serving, and reaching our community." },
];

export default function Give() {
  return (
    <div data-testid="give-page">
      <section className="relative py-28 lg:py-40 overflow-hidden bg-[#003B5C]">
        <div className="absolute inset-0">
          <div className="absolute -top-40 -right-32 w-[500px] h-[500px] rounded-full bg-[#D4A437]/8" />
          <div className="absolute -bottom-40 -left-32 w-[500px] h-[500px] rounded-full bg-[#D4A437]/5" />
          <div className="absolute inset-0" style={{
            backgroundImage: "repeating-linear-gradient(135deg, rgba(255,255,255,0.025) 0 1px, transparent 1px 60px)",
          }} />
        </div>
        <div className="relative max-w-5xl mx-auto px-6 sm:px-8 lg:px-12 text-center">
          <Sparkles size={32} className="text-[#D4A437] mx-auto mb-6" />
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#D4A437] mb-6">Give</div>
          <h1 className="font-display text-5xl sm:text-6xl lg:text-8xl font-black tracking-tighter leading-[0.95] text-white">
            Give with<br /><span className="text-[#D4A437]">purpose.</span>
          </h1>
          <p className="mt-10 text-lg sm:text-xl text-white/80 max-w-2xl mx-auto leading-relaxed">
            Giving is an act of worship and gratitude. Through faithful generosity, lives are
            impacted, ministries grow, and communities are served.
          </p>
          <div className="mt-12 flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href={GIVE_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="give-online-cta"
              className="inline-flex items-center justify-center gap-2 bg-[#D4A437] text-white px-10 py-5 rounded-sm font-semibold text-lg hover:bg-[#B88C2A] transition-all group"
            >
              Give Online <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />
            </a>
            <a
              href="#learn-more"
              data-testid="give-learn-more-cta"
              className="inline-flex items-center justify-center gap-2 border border-white/40 text-white px-10 py-5 rounded-sm font-semibold text-lg hover:bg-white/10 transition-all"
            >
              Learn More
            </a>
          </div>
        </div>
      </section>

      <section id="learn-more" className="py-24 lg:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="max-w-3xl mb-16">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#D4A437] mb-4">Your Impact</div>
            <h2 className="font-display text-4xl sm:text-5xl font-bold tracking-tight text-[#003B5C] leading-tight">
              Where your generosity goes.
            </h2>
            <p className="mt-6 text-lg text-[#1E1E1E]/75 leading-relaxed">
              Thank you for partnering with us in sharing God's love. Every gift — large or small — fuels real ministry and lasting change.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {IMPACT.map(({ icon: Icon, title, desc }) => (
              <div key={title} data-testid={`give-impact-${title.toLowerCase().replace(/\s+/g, "-")}`} className="p-8 border border-gray-200 rounded-sm hover:border-[#D4A437] hover:shadow-md transition-all">
                <div className="w-12 h-12 bg-[#003B5C] rounded-sm flex items-center justify-center mb-5">
                  <Icon size={22} className="text-[#D4A437]" />
                </div>
                <h3 className="font-display text-xl font-bold text-[#003B5C] mb-3">{title}</h3>
                <p className="text-sm leading-relaxed text-[#1E1E1E]/70">{desc}</p>
              </div>
            ))}
          </div>

          <div className="mt-20 bg-[#F7F8FA] border border-gray-100 rounded-sm p-10 lg:p-14 text-center">
            <blockquote className="font-display text-2xl sm:text-3xl font-medium text-[#003B5C] leading-snug max-w-3xl mx-auto">
              "Each of you should give what you have decided in your heart to give, not reluctantly or under compulsion, for God loves a cheerful giver."
            </blockquote>
            <div className="mt-5 text-sm font-bold uppercase tracking-[0.2em] text-[#D4A437]">2 Corinthians 9:7</div>
          </div>
        </div>
      </section>
    </div>
  );
}
